import {
    Collection, DOMUtil, DynamicLoader, FileUtil, HorizontalAlignment, LogManager, MixinUtil, Model, ModelRow,
    StringUtil, VerticalAlignment
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { BorderType } from "../../base/BorderType";
import { getCurrentDataSourceMode } from "../../base/ComponentDataLink";
import { ComponentPropDefinition, ComponentPropDefinitions, ComponentProps } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { PrintableEvent } from "../../events/PrintableEvent";
import { PrintableLabel } from "../label/PrintableLabel";
import { PrintableMixin } from "../../mixins/PrintableMixin";
import { PrintableMixinInterface } from "../../mixins/PrintableMixinInterface";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { DataSourceMode } from "../../databinding/DataSource";
import { DomDragEvent } from "../../events/DomEvent";
import { DragEvent } from "../../events/DragEvent";
import { Button } from "../button/Button";
import { ButtonVariant } from "../button/ButtonVariant";
import { CompoundComponent } from "../compound/CompoundComponent";
import { Image } from "../image/Image";
import { ImageType } from "../image/ImageType";
import { ImageViewer } from "../../page/imageViewer/ImageViewer";
import { ImageViewerValues } from "../../page/imageViewer/ImageViewerValues";
import { Label } from "../label/Label";
import { AttachmentFileType } from "./AttachmentFileType";
import { AttachmentPropDefinitions, AttachmentProps } from "./AttachmentProps";
import { ListenerListDef } from "../../base/ListenerListDef";
import { FileUploadEvent, FileUploadListener } from "../../events/FileUploadEvent";
import { FileRemovalEvent, FileRemovalListener } from "../../events/FileRemovalEvent";

const log = LogManager.getLogger("components.Attachment");

export class Attachment extends CompoundComponent implements AttachmentProps {
    private static readonly fileRemovalListenerDef: ListenerListDef = { listName: "_fileRemovalListeners" };
    private static readonly fileUploadListenerDef: ListenerListDef = { listName: "_fileUploadListeners" };
    allowedFileTypes: AttachmentFileType[];
    fileNameField: string;
    fileName: string;
    fileBase64: string;
    attachmentId: string;
    idField: string;
    private _maxSize: number;
    displayThumbnail: boolean;
    customThumbnailSize: number = 0;
    allowRemoveThumbnailOption: true;
    public validationCallback: (file: File) => string;

    private _caption: string;
    private _loading: boolean;
    private _loadMessage: string;
    private enterCount: number = 0;
    private _labelCaption: Label;
    private _buttonRemove: Button;
    private _svgImage: Image;
    private _thumbnail: Image;
    private _showSVG: boolean;
    private printableLabel: PrintableLabel;
    private _nonRemoveClickListener: (event) => void;
    private _removeClickListener: (event) => void;

    constructor(props?: Partial<AttachmentProps>) {
        super(props, false);
        this._nonRemoveClickListener = event => this.imageClicked();
        this._removeClickListener = event => this.removeAttachment();
        this._svgImage = new Image({ fillRow: true, fillHeight: true, name: ImageName.designer_attachment, maxHeight: 100, align: HorizontalAlignment.CENTER });
        this._svgImage.addClickListener(this._nonRemoveClickListener);
        this._thumbnail = new Image({ imageType: ImageType.IMG, height: props.customThumbnailSize == undefined ? 180 : props.customThumbnailSize, fillRow: true, align: HorizontalAlignment.CENTER, preserveAspectRatio: true });
        this._thumbnail.addClickListener(this._nonRemoveClickListener);
        this._labelCaption = new Label({ fillRow: true, fontSize: "large", align: HorizontalAlignment.CENTER, rowBreak: false, allowSelect: false });
        this._labelCaption.addClickListener(this._nonRemoveClickListener);
        this.createPrintableLabel();
        this._buttonRemove = new Button({ variant: ButtonVariant.round, imageName: ImageName.x, visible: false, cursor: null, margin: 0 });
        this._buttonRemove.addClickListener(this._removeClickListener);
        this.add(this._svgImage, this._labelCaption, this._buttonRemove, this.printableLabel);
        this.printableDuringSearch = true;
        this._shouldAddDesignerContainerProperties = false;
        this.setProps({
            ...this.enabledBorderProps,
            color: "primary",
            padding: 8,
            verticalAlign: VerticalAlignment.CENTER,
            ...props
        });
        this._syncDisplay();
        this.syncInnerComponentIds();
    }

    private createPrintableLabel() {
        // Cannot instantiate using class directly, causes circular reference
        const printableLabelClass = DynamicLoader.getClassForPath("components/components/label/PrintableLabel");
        this.printableLabel = new printableLabelClass(this, {
            fillRow: true,
            fontSize: "large",
            align: HorizontalAlignment.CENTER,
            rowBreak: false,
            allowSelect: false,
            visible: false
        });
        this.printableLabel.addClickListener(this._nonRemoveClickListener);
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._svgImage, "svgImage");
        this.setInnerComponentId(this._thumbnail, "thumbnail");
        this.setInnerComponentId(this._labelCaption, "labelCaption");
        this.setInnerComponentId(this._buttonRemove, "buttonRemove");
        this.printableLabel?.syncId();
    }

    override doAfterDeserialize() {
        if (this._designer == null) {
            this.addDragEnterListener(event => this.dragEnter());
            this.addDragOverListener(event => this.dragOver(event));
            this.addDragLeaveListener(event => this.dragLeave());
            this.addDropListener(event => this.dropped(event));
        }
    }

    dragEnter() {
        if (!this.printable && this.enabled === true) {
            this.backgroundColor = "background4";
            this.color = "primary.light";
            this.enterCount++;
        }
    }

    dragOver(event: DragEvent) {
        if (!this.printable && this.enabled === true)
            event.consume();
    }

    dragLeave() {
        if (!this.printable && this.enabled === true) {
            this.enterCount--;
            if (this.enterCount <= 0)
                this.resetMouseOver();
        }
    }

    dropped(event: DragEvent) {
        if (!this.printable && this.enabled === true) {
            event.consume();
            const files = DOMUtil.getDraggedFiles(event.domEvent as DomDragEvent);
            this.resetMouseOver();
            if (files.length > 0)
                this.setAttachmentWithUI(files[0]);
        }
    }

    async setAttachmentWithUI(file: File) {
        let invalidAttachmentMessage: string = null;
        if (this.validationCallback != null)
            invalidAttachmentMessage = this.validationCallback(file);
        if (StringUtil.isEmptyString(invalidAttachmentMessage) === true) {
            const maxSize = this.maxSize;
            if (maxSize != null && file.size > maxSize)
                invalidAttachmentMessage = "The file is too big to attach. The file must be smaller than " + this.maxSize / (1024 * 1024) + "MB.";
        }
        if (StringUtil.isEmptyString(invalidAttachmentMessage) === true) {
            this.setFile(file);
            this.fireFileUploadListeners(new FileUploadEvent(this, file));
        }
        else
            this.showAdhocTooltip(invalidAttachmentMessage, { shaking: true, timeout: 5000 });
    }

    get maxSize(): number {
        return this._maxSize == null ? this._boundField?.length : this._maxSize;
    }

    set maxSize(value: number) {
        this._maxSize = value;
    }

    removeAttachment() {
        if (this.enabled === true) {
            const fileName = this.fileName;
            this.fileName = null;
            this.fileBase64 = null;
            this.updateIfBound();
            this._syncDisplay();
            this.fireFileRemovalListeners(new FileRemovalEvent(this, fileName));
        }
    }

    setFile(file: File) {
        this.fileName = file.name;
        const reader = new FileReader();
        this.loading = true;
        this._loadMessage = null;
        this.fileBase64 = null;
        reader.readAsDataURL(file);
        this._syncDisplay();
        reader.onload = () => {
            let base64 = reader.result as string;
            base64 = StringUtil.stringAfter(base64, ",");
            this.fileBase64 = base64;
            this.loading = false;
            this.updateIfBound();
            this._syncDisplay();
        };
        reader.onerror = (error) => {
            this._loadMessage = "Error loading " + file.name;
            this.loading = false;
            this._syncDisplay();
        };
    }

    private resetMouseOver() {
        this.backgroundColor = null;
        this.color = "subtle.light";
        this.enterCount = 0;
    }

    override _fieldBindingChanged() {
        super._fieldBindingChanged();
        this.updateThumbnailIfApplicable();
    }

    private imageClicked() {
        if (this.fileBase64 == null) {
            if (this.fileName == null) {
                if (!this.printable && this.enabled === true) {
                    FileUtil.showOpenFileDialog().then(file => {
                        if (file != null)
                            this.setAttachmentWithUI(file);
                    });
                }
            }
            else
                this.fetchBinaryDynamically(true);
        }
        else
            ImageViewer.viewOrDownload(this.imageViewerValues);
    }

    get imageViewerValues(): ImageViewerValues {
        return new ImageViewerValues(null, this.fileBase64, this.fileName);
    }

    fetchBinaryDynamically(shouldDownload: boolean) {
        if (this._boundField == null)
            return;
        this.loading = true;
        this._loadMessage = "Downloading...";
        this._syncDisplay();
        if (this._boundField.tableName == null) {
            log.error("Cannot fetch binary for attachment because the table name is null.");
            return;
        }
        Model.searchSingleRecord("lib/blob-detail", { table_name: this._boundField.tableName, field_name: this.field, id: this.attachmentId }).then(row => {
            this.loading = false;
            this._loadMessage = null;
            this.fileBase64 = row.get("binary");
            this._syncDisplay();
            if (shouldDownload) {
                ImageViewer.viewOrDownload(this.imageViewerValues);
            }
        }).catch(error => {
            this.loading = false;
            this._loadMessage = null;
            this._syncDisplay();
            ReflectiveDialogs.showError(error);
        });
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (data == null) {
            this.fileName = null;
            this.fileBase64 = null;
            this.attachmentId = null;
        } else {
            if (this.fileNameField != null)
                this.fileName = data.get(this.fileNameField);
            if (this.field != null)
                this.fileBase64 = data.get(this.field);
            if (this.idField != null)
                this.attachmentId = data.get(this.idField);
            // this.updateThumbnailIfApplicable();
        }
        this._syncDisplay();
    }

    private updateThumbnailIfApplicable() {
        if (this.displayThumbnail === true && this.isThumbnailAvailableForFileType())
            this.fetchBinaryDynamically(false);
    }

    updateIfBound() {
        if (this._boundField != null) {
            let row = null;
            if (this.dataSource != null && this.dataSource.activeRow != null)
                row = this.dataSource.activeRow;
            else if (this.boundRow != null)
                row = this.boundRow;
            if (row != null)
                this.updateBoundData(row, DataSourceMode.UPDATE);
        }
    }

    updateBoundData(data: ModelRow, mode: DataSourceMode) {
        if (this.fileNameField != null && mode !== DataSourceMode.SEARCH)
            data.set(this.fileNameField, this.fileName, this);
        if (this.field != null)
            data.set(this.field, this.fileBase64, this);
    }

    _serializeNonProps(): string {
        return "";
    }

    get caption(): string {
        return this._caption;
    }

    set caption(value: string) {
        this._caption = value;
        this._syncDisplay();
    }

    set showSVG(value: boolean) {
        this._showSVG = value;
        this._syncDisplay();
    }

    get showSVG(): boolean {
        return this._showSVG;
    }

    get loading() {
        return this._loading == null ? false : this.loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this._labelCaption.imageProps = { rotate: value === true };
        this._labelCaption.imageName = this._loading === true ? ImageName.spinner : null;
    }

    _syncDisplay() {
        this._labelCaption.text = this._loadMessage || this.fileName || this._caption || (this.enabled === true ? "Add Attachment" : null);
        this.printableLabel.text = this._loadMessage || this.fileName || this._caption || "";
        this.syncPrintableLabelClickListener();
        this._buttonRemove.visible = (this.fileName != null || this.fileBase64 != null) && this.enabled === true && this.printable !== true && this.allowRemoveThumbnailOption;
        this._svgImage.visible = this._showSVG === true && getCurrentDataSourceMode(this) !== DataSourceMode.SEARCH;
        if (this._svgImage.visible) {
            if (this.fileBase64 != null && this.isThumbnailAvailableForFileType()) {
                this._thumbnail.imageBytes = this.fileBase64;
                this.setThumbnailVisible(this.displayThumbnail);
            } else if (this.fileName == null) {
                this._svgImage.name = ImageName.designer_attachment;
                this.setThumbnailVisible(false);
            } else {
                this._svgImage.name = ImageName.document;
                this.setThumbnailVisible(false);
            }
        }
    }

    private isThumbnailAvailableForFileType() {
        if (this.fileBase64 != null && this.fileName == null)
            return true;
        else if (this.fileName == null)
            return false;
        const fileName = this.fileName.toLowerCase();
        return fileName.endsWith(".jpg") ||
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".png") ||
            fileName.endsWith(".gif");
    }

    setThumbnailVisible(value: boolean) {
        if (value && !this.contains(this._thumbnail)) {
            this.replace(this._svgImage, this._thumbnail);
        } else if (!value && !this.contains(this._svgImage))
            this.replace(this._thumbnail, this._svgImage);
    }

    override setProps(props: Partial<AttachmentProps>) {
        super.setProps(props);
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return AttachmentPropDefinitions.getDefinitions();
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "maxSize")
            return this._maxSize == null ? this._boundField?.length : this._maxSize;
        if (this.printable === true || this.printableDuringSearch == true) {
            if (prop.name === "minHeight")
                return 32;
            else if (prop.name === "marginTop")
                return 4;
        }

        return super.determinePropertyDefaultValue(prop);
    }

    override getFieldNames(): string[] {
        const result = [];
        if (this.fileNameField != null)
            result.push(this.fileNameField);
        return result;
    }

    override get serializationName() {
        return "attachment";
    }

    override get properName(): string {
        return "Attachment";
    }

    override getBasicValue(): any {
        return this.fileName;
    }

    override dataSourceModeChanged(mode: DataSourceMode) {
        super.dataSourceModeChanged(mode);
        this.syncPrintable();
    }

    override _applyEnabled(value: boolean) {
        if (this.printable === true) {
            return;
        }
        this.setProps(value === true ? this.enabledBorderProps : this.disabledBorderProps);
    }

    protected _applyPrintable(value: boolean) {
        if (value === true) {
            this.setProps(this.disabledBorderProps);
            this._configureImageListeners(false);
            this._labelCaption.visible = false;
            this._labelCaption.removeClickListener(this._nonRemoveClickListener);
            this.printableLabel.visible = true;
            this.syncPrintableLabelClickListener();
            this._buttonRemove.removeClickListener(this._removeClickListener);
        }
        else {
            this.setProps(this.enabledBorderProps);
            this._configureImageListeners(true);
            this._labelCaption.visible = true;
            this._labelCaption.addClickListener(this._nonRemoveClickListener);
            this.printableLabel.visible = false;
            this.printableLabel.removeClickListener(this._nonRemoveClickListener);
            this._buttonRemove.addClickListener(this._removeClickListener);
        }
        this.marginTop = 4;
        this.minHeight = 32;
        this._syncDisplay();
        this._syncEnabled();
        this.firePrintableListeners(new PrintableEvent(this));
    }

    override _syncEnabled() {
        super._syncEnabled();
        this.printableLabel.enabled = this.enabled && getCurrentDataSourceMode(this) !== DataSourceMode.SEARCH;
    }

    private syncPrintableLabelClickListener() {
        if (!StringUtil.isEmptyString(this.printableLabel.text))
            this.printableLabel.addClickListener(this._nonRemoveClickListener);
        else
            this.printableLabel.removeClickListener(this._nonRemoveClickListener);
    }

    private _configureImageListeners(activate: boolean) {
        if ((activate === false && StringUtil.isEmptyString(this.printableLabel.text)) || getCurrentDataSourceMode(this) === DataSourceMode.SEARCH) {
            this._svgImage.removeClickListener(this._nonRemoveClickListener);
            this._thumbnail.removeClickListener(this._nonRemoveClickListener);
            return;
        }
        this._svgImage.addClickListener(this._nonRemoveClickListener);
        this._thumbnail.addClickListener(this._nonRemoveClickListener);
    }

    addFileRemovalListener(value: FileRemovalListener) {
        this.addEventListener(Attachment.fileRemovalListenerDef, value);
    }

    removeFileRemovalListener(value: FileRemovalListener) {
        this.removeEventListener(Attachment.fileRemovalListenerDef, value);
    }

    fireFileRemovalListeners(removalEvent: FileRemovalEvent) {
        this.fireListeners(Attachment.fileRemovalListenerDef, removalEvent);
    }

    addFileUploadListener(value: FileUploadListener) {
        this.addEventListener(Attachment.fileUploadListenerDef, value);
    }

    removeFileUploadListener(value: FileUploadListener) {
        this.removeEventListener(Attachment.fileUploadListenerDef, value);
    }

    fireFileUploadListeners(uploadEvent: FileUploadEvent) {
        this.fireListeners(Attachment.fileUploadListenerDef, uploadEvent);
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "fileRemoval": { ...Attachment.fileRemovalListenerDef },
            "fileUpload": { ...Attachment.fileUploadListenerDef },
            ...MixinUtil.getMixinListenerDefs(this)
        };
    }

    private get enabledBorderProps(): Partial<ComponentProps> {
        return {
            borderWidth: 2,
            borderType: BorderType.DASHED,
            borderRadius: 4
        };
    }

    private get disabledBorderProps(): Partial<ComponentProps> {
        return {
            borderWidth: 0,
            borderType: null,
            borderRadius: 4
        };
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Attachment, [ PrintableMixin ]);

// TypeScript interface declaration for mixin integration
export interface Attachment extends PrintableMixinInterface {
}

ComponentTypes.registerComponentType("attachment", {
    constructor: Attachment.prototype.constructor,
    isCompound: true
});
